import React, { Component, ReactNode, ErrorInfo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Container,
  Stack,
  Collapse,
  Paper
} from '@mui/material';
import AlertTriangle from '@mui/icons-material/Warning';
import RefreshCw from '@mui/icons-material/Refresh';
import Home from '@mui/icons-material/Home';
import { logger } from '@/shared/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // In production, you could send this to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 2,
          }}
        >
          <Container maxWidth="sm">
            <Card sx={{ boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'error.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <AlertTriangle color="error" />
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h2">
                      Oops! Something went wrong
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      The application encountered an unexpected error
                    </Typography>
                  </Box>
                </Box>

                {/* Error details in development mode */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Error: {this.state.error.message}</AlertTitle>
                    {this.state.errorInfo && (
                      <>
                        <Button
                          size="small"
                          onClick={this.toggleDetails}
                          sx={{ mt: 1 }}
                        >
                          {this.state.showDetails ? 'Hide' : 'View'} stack trace
                        </Button>
                        <Collapse in={this.state.showDetails}>
                          <Paper
                            sx={{
                              mt: 2,
                              p: 1,
                              bgcolor: 'grey.900',
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            <Typography
                              component="pre"
                              variant="caption"
                              sx={{
                                color: 'grey.100',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                              }}
                            >
                              {this.state.errorInfo.componentStack}
                            </Typography>
                          </Paper>
                        </Collapse>
                      </>
                    )}
                  </Alert>
                )}

                {/* Action buttons */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    onClick={this.handleReset}
                    startIcon={<RefreshCw />}
                    fullWidth
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={this.handleReload}
                    fullWidth
                  >
                    Reload Page
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={this.handleGoHome}
                    startIcon={<Home />}
                    fullWidth
                  >
                    Go Home
                  </Button>
                </Stack>

                {/* Support message */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  display="block"
                  sx={{ mt: 3 }}
                >
                  If this problem persists, please contact support
                </Typography>
              </CardContent>
            </Card>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for functional components
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
}